import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage.service';
import { EssentialsService, mapEssentialToCard } from '@core/services/essentials/essentials.service';
import { EMPTY, Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';

type EssentialsTab = 'imperdonables' | 'infaltables' | 'ofertas';
type ListedTab = Exclude<EssentialsTab, 'ofertas'>;

interface EssentialsTabConfig {
  id: EssentialsTab;
  label: string;
  icon: string;
  hidden?: boolean;
}

const UNMISSABLES_GROUP = '2';
const ESSENTIALS_GROUP = '3';
const SEE_ALL_LIMIT = 30;
const CLIENT_HOME = '/tienda';
const SELLER_HOME = '/main/inicio';

@Component({
  selector: 'app-the-essentials',
  templateUrl: './the-essentials.component.html',
  styleUrls: ['./the-essentials.component.scss']
})
export class TheEssentialsComponent implements OnInit, OnDestroy {
  readonly skeletonCards = Array.from({ length: 15 });
  readonly skeletonTabs = Array.from({ length: 2 });
  // Zona de Ofertas se oculta hasta que se integre su listado
  readonly tabs: EssentialsTabConfig[] = [
    { id: 'imperdonables', label: 'Imperdonables', icon: 'unmissables' },
    { id: 'infaltables', label: 'Infaltables', icon: 'essentials' },
    { id: 'ofertas', label: 'Zona de Ofertas', icon: 'offers', hidden: true },
  ];
  // Datos para la analítica de cada listado
  readonly listData: Record<ListedTab, { name: string; id: string }> = {
    imperdonables: { name: 'Imperdonables - Ver todos', id: 'IMP2' },
    infaltables: { name: 'Los Infaltables - Ver todos', id: 'INF2' },
  };

  products: Record<ListedTab, any[]> = { imperdonables: [], infaltables: [] };
  visibleTabs: EssentialsTabConfig[] = [];
  activeTab: ListedTab = 'imperdonables';
  loading = true;
  user: any;
  clientSelect: any;
  sectorSelect: any;
  showScrollTop = false;
  // Declara un Subject para controlar la cancelación
  private readonly onDestroy$ = new Subject<void>();

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly _essentialsService: EssentialsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) { }

  get isSeller(): boolean {
    return this.user?.type === 'Vendedor';
  }

  ngOnInit() {
    this.user = this.localStorageService.getItem('user');
    this.clientSelect = this.localStorageService.getItem('clientSelect');
    this.sectorSelect = this.localStorageService.getItem('sectorSelect');

    // Se piden ambos listados para saber qué pestañas mostrar; luego la pestaña viaja en la URL (?tab=)
    forkJoin({
      imperdonables: this.loadGroup(UNMISSABLES_GROUP),
      infaltables: this.loadGroup(ESSENTIALS_GROUP),
    })
      .pipe(
        switchMap((products) => {
          this.products = products;
          this.visibleTabs = this.tabs.filter((tab) => !tab.hidden && products[tab.id as ListedTab].length > 0);
          this.loading = false;

          // Sin productos en ninguna pestaña la vista no tiene sentido: vuelve al inicio
          if (this.visibleTabs.length === 0) {
            this.router.navigateByUrl(this.isSeller ? SELLER_HOME : CLIENT_HOME, { replaceUrl: true });
            return EMPTY;
          }
          return this.route.queryParamMap;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((params) => {
        this.activeTab = this.toTab(params.get('tab'));
      });
  }

  selectTab(tab: EssentialsTab) {
    if (tab === this.activeTab) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.showScrollTop = window.scrollY > 0;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  trackBySku(_: number, card: any) {
    return card.sku;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  // Si el servicio falla o no trae data, la pestaña se trata como vacía
  private loadGroup(group: string): Observable<any[]> {
    return this._essentialsService
      .getEssentialsByGroup({ ...this.buildBody(), group, limit: SEE_ALL_LIMIT })
      .pipe(
        map((res: any) => (Array.isArray(res) ? res.map(mapEssentialToCard) : [])),
        catchError(() => of([]))
      );
  }

  // El vendedor consulta con los datos del cliente seleccionado
  private buildBody() {
    const source = this.isSeller ? this.clientSelect : this.sectorSelect;
    return {
      code: this.isSeller ? this.clientSelect?.code : this.user?.code,
      sector: source?.sector,
      zone: source?.zone,
      schema: source?.schema,
      segment: source?.segment,
    };
  }

  // Una pestaña desconocida o sin productos abre la primera disponible
  private toTab(value: string | null): ListedTab {
    const tab = this.visibleTabs.find((item) => item.id === value) ?? this.visibleTabs[0];
    return tab.id as ListedTab;
  }
}
